import { backendHost as configuredBackendHost } from './config';

enum Constants {
  /** Number of results to download per retrieval, used in various places */
  ResultCount = 50,
  HeaderHideBreakpoint = 90,
  // No credentials here: every client id and host comes from the
  // environment through src/config.ts.
  MsGraphDlUrlProp = '@microsoft.graph.downloadUrl',
  DataFolder = 'Fadila',
  ActivePlaylistName = '♫ Now Playing ♫',
  ActivePlaylistId = 'activeplaylist',
  /** LocalStorage key */
  ActivePlaylistKey = 'activePlaylist',
  /**
   * LocalStorage key: the id of the library playlist the queue was last
   * started from; absent when that was a folder, a search or an embed. Read
   * by playlistToShow for ctrl+L. Kept apart from the queue itself so that
   * the queue's stored shape, which its loader is strict about, is unchanged.
   */
  ActivePlaylistSourceKey = 'activePlaylistSource',
  LibraryName = '📚 Library',
  LibraryId = 'library',
  PodcastFolderName = 'Podcasts',
  NewPlaylistName = '» New Playlist «',
}
export default Constants;

export enum Tasks {
  addFolderToPlaylist = 'addFolderToPlaylist',
  resetAppDataFolder = 'resetAppDataFolder',
  downloadAppDataFolder = 'downloadAppDataFolder',
}
export enum Keys {
  saved,
  storageProviderNotLoggedIn,
  dropboxNotLoggedIn,
  spotifyNotLoggedIn,
  googleAppDataDuplicates,
  libraryPathNotConfigured,
  libraryLoadError,
  ensurePathStringConformity,
  notPodcastItem,
  savedPodcastItem,
  playlistNotFound,
  errorAddingPlaylistToLibrary,
  errorAddingFolderToLibrary,
  invalidTrackType,
  driveRequiresServiceWorker,
  googleSignInNeedsButton,
  googleScopeNotGranted,
  driveFilesPermissionMissing,
  youTubeWriteAccessMissing,
  youTubeWriteAccessHint,
  youTubeSyncLinked,
  youTubeSyncUnlinked,
  youTubeSyncFailed,
  youTubeSyncNotSignedIn,
  soundCloudSyncLinked,
  soundCloudSyncUnlinked,
  soundCloudSyncFailed,
}
const strings: {
  [P in Keys]: string;
} = {
  [Keys.dropboxNotLoggedIn]: 'Please log in to Dropbox and try again',
  [Keys.spotifyNotLoggedIn]: 'Please log in to Spotify and try again',
  // Says what it is rather than what it is not: the old wording named a
  // "currently configured storage provider" the user has no name for and
  // asked them to log in without saying where. The usual cause is a lapsed
  // token an hour after sign-in, which looks to the player exactly like
  // never having signed in at all.
  [Keys.storageProviderNotLoggedIn]:
    'Your sign-in to the cloud storage holding your library has run out, so nothing stored there can be played or saved. Sign in again from the setup screen.',
  [Keys.libraryPathNotConfigured]: 'Please configure library path in settings',
  [Keys.ensurePathStringConformity]: 'Path string should start with /',
  [Keys.saved]: 'Saved!',
  [Keys.notPodcastItem]: 'Incorrect track type, podcast item needed',
  [Keys.savedPodcastItem]:
    'Successfully saved podcast to library, updated all playlists with saved item',
  [Keys.googleAppDataDuplicates]:
    'Duplicate app data files found! Reset GoogleDrive AppData in settings may fix.',
  [Keys.playlistNotFound]: 'Unable to find playlist, please try again.',
  [Keys.errorAddingPlaylistToLibrary]: 'Error adding playlist to library',
  [Keys.errorAddingFolderToLibrary]: 'Error adding folder to library',
  [Keys.libraryLoadError]: 'Unable to load library, please reload to try again',
  [Keys.invalidTrackType]: "Invalid track type, can't add to library",
  [Keys.driveRequiresServiceWorker]:
    'Google Drive is unavailable on this device: the background worker that authorises Drive downloads could not start. Other storage providers still work. On iOS, try Safari, add the app to your home screen, and ensure private browsing is off.',
  [Keys.googleSignInNeedsButton]:
    'Signing in to Google means leaving the page for a moment, so Fadila only does it from a sign-in button. Use the one on the setup screen.',
  [Keys.googleScopeNotGranted]:
    'Signed in to Google, but the permission Fadila needs was not granted. Without it the library cannot be stored in your Drive.',
  [Keys.driveFilesPermissionMissing]:
    'Signed in to Google, but Fadila has not been given permission to read your Drive files - only its own settings folder. Open Google Drive from the menu and press the sign-in button there to grant it.',
  [Keys.youTubeWriteAccessMissing]:
    'Fadila has not been given permission to change your YouTube playlists.',
  [Keys.youTubeWriteAccessHint]:
    'Changes made in Fadila will only reach YouTube once you grant write access. Open the YouTube page and press "Allow YouTube changes".',
  [Keys.youTubeSyncLinked]: 'Playlists linked. Syncing now.',
  [Keys.youTubeSyncUnlinked]: 'Playlists unlinked. Nothing was removed on either side.',
  [Keys.youTubeSyncFailed]: 'YouTube playlist sync failed.',
  [Keys.youTubeSyncNotSignedIn]: 'Sign in to YouTube to sync playlists.',
  [Keys.soundCloudSyncLinked]: 'Playlists linked. Syncing now.',
  [Keys.soundCloudSyncUnlinked]: 'Playlists unlinked. Nothing was removed on either side.',
  [Keys.soundCloudSyncFailed]: 'SoundCloud playlist sync failed.',
};

export function messages(key: Keys) {
  return strings[key];
}

/**
 * Tests for a mac desktop computer.
 * This is used to make assumptions on intended use of ctrlKey and metakey.
 * We switch shortcuts on mac computers.
 */
export const isMac = /mac/i.test(navigator.platform);

export const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

export const backendHost = () => configuredBackendHost;

/**
 * Default character to use for width calculations
 * F.x. when calculating default width of columns with calcFromCharCount
 * render an element filled with x * characterForWidthCalc
 */
export const characterForWidthCalc = '0';
